import { useEffect, useState } from "react";
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket";
import { HOST_WSS } from "src/config/config";
import { WssActionTypes, WssState, useWssDispatch, useWssState } from "src/contexts/WSSContext";
import { WssPayload, resolveWssEvent } from "src/modules/shared/services/wssResolver";
import { Storage } from "src/utils/storage";
import { WSS, WSSMessage, WSSMessageStatus } from "src/utils/wss";

export interface WssHook {
    lastMessage: MessageEvent | null;
    setAllSeen: () => void;
    wssState: WssState | null;
    initializeWss: () => void;
    closeWss: () => void;
}

const useWss = (): WssHook => {
    const userStorage = Storage.getItem("userState");

    // from context
    const wssState = useWssState();
    const dispatch = useWssDispatch();

    const [socketUrl, setSocketUrl] = useState<string | null>(null);

    // initialize library
    const { sendMessage, readyState, lastMessage } = useWebSocket(socketUrl, {
        shouldReconnect: () => true,
        onMessage: (event) => {
            // console.info("onMessage", event.data);
            if (event?.data) {
                const currentStorageList = WSS.getCurrentStorage();
                const eventDataObject: WssPayload = JSON.parse(event?.data);
                const eventData: string = event?.data;
                resolveWssEvent(eventDataObject);
                WSS.pushMessage(eventData);

                if (
                    !currentStorageList.find(
                        (csl: any) => JSON.stringify(csl.payload) === eventData
                    )
                ) {
                    const messageEvent: WSSMessage = {
                        timestamp: Date.now(),
                        payload: { ...eventDataObject },
                        status: WSSMessageStatus.NEW,
                    };
                    const reversed = [messageEvent]
                        .concat((wssState?.lastMessages || []) as WSSMessage[])
                        .reverse();

                    dispatch({
                        type: WssActionTypes.SET_MESSAGES,
                        payload: reversed,
                    });
                }
            }
        },
    });

    // init host
    useEffect(() => {
        if (!socketUrl && userStorage?.accessToken) {
            setSocketUrl(HOST_WSS);
        }
    }, []);

    // handshake
    useEffect(() => {
        if (readyState === 1 && userStorage?.accessToken) {
            sendMessage(
                JSON.stringify({
                    action: "authorization",
                    token: userStorage?.accessToken,
                })
            );
        }
    }, [readyState]);

    //setSeenAll
    const setAllSeen = () => {
        WSS.setAllSeen();
        dispatch({ type: WssActionTypes.SET_ALL_SEEN });
    };

    const initializeWss = () => {
        if (userStorage?.accessToken && !socketUrl) {
            setSocketUrl(HOST_WSS);
        }
    };

    const closeWss = () => {
        setSocketUrl(null);
        console.info("wss:closed");
    };

    return { lastMessage, setAllSeen, wssState, initializeWss, closeWss };
};

export default useWss;
